/**
 * Faixa horizontal das páginas públicas.
 *
 * Separada do padding porque o corpo do diretório (lista + mapa) precisa da MESMA
 * faixa do header, mas com padding próprio: sem isso, em telas mais largas que o
 * teto o header centraliza e ganha margem enquanto a lista começa na borda, e o
 * breadcrumb não alinha com a logo.
 *
 * O teto (e não `w-full`) evita o conteúdo grudar nas bordas em telas muito
 * largas; abaixo dele, na prática, ocupa tudo.
 */
export const PUBLIC_BAND = 'mx-auto w-full max-w-[1760px]'

/** Padding lateral padrão. Quem alinha com o header precisa usar o mesmo. */
export const PUBLIC_GUTTER = 'px-5 sm:px-6 lg:px-10'

/** Faixa + padding: o que header e rodapé usam. */
export const PUBLIC_CONTAINER = `${PUBLIC_BAND} ${PUBLIC_GUTTER}`

/** Azul da marca. Mesma cor do default de `schools.primary_color`. */
export const BEWAVE_BLUE = '#0ea5e9'

/**
 * A ilustração da marca ao fundo das telas de autenticação — o login/senha do
 * admin (AuthLayout) e o login/cadastro do criador (CreatorShell). Constante e
 * não duplicada porque são o MESMO momento do produto, e um fundo diferente de
 * cada lado faria as duas contas parecerem dois sites.
 *
 * Decorativa, daí `bg-*` e não um `<img>`: não há o que descrever num `alt`, e
 * o cartão por cima carrega o conteúdo de verdade.
 *
 * O `bg-cover`/`lg:bg-contain` é o que a torna responsiva. A arte é 16:9 com o
 * letreiro "BEWAVE" no topo e a praia embaixo; num monitor largo o miolo fica
 * baixo e comprido (3:1 ou mais), e `bg-cover` sozinho esticava pela largura e
 * comia o letreiro e a praia — sobrava o vazio do meio. De `lg` para cima ela
 * passa a caber inteira, e `#efefef` (o papel da própria arte, amostrado do
 * arquivo) preenche as laterais sem emenda visível. Abaixo de `lg` a tela é
 * alta e estreita: ali `cover` continua certo, e é o que já era.
 */
export const AUTH_BACKDROP =
  "bg-[#efefef] bg-[url('/auth/background.webp')] bg-cover bg-center bg-no-repeat lg:bg-contain"

/**
 * O BeWave Explore — o diretório de escolas.
 *
 * Constante e não literal porque meia dúzia de lugares apontam para cá (header,
 * rodapé, as duas seções da landing, a própria página) e o endereço já mudou
 * DUAS vezes: era `/schools`, virou `/explore`, e agora que o Explore tem host
 * próprio (explore.bewave.surf) é a RAIZ dele.
 *
 * Por isso `/` e não uma URL absoluta: todo lugar que usa esta constante já
 * roda dentro do host do Explore, onde o link é relativo mesmo. O único ponto
 * que precisa atravessar de host é o link do rodapé na landing comercial — e
 * lá a origem vem do servidor, via a prop `exploreOrigin` do BewaveFooter.
 *
 * A página de cada negócio mora na raiz também: `/:slug` (os endereços antigos
 * `/explore/:slug` e `/schools/:slug` respondem 301).
 */
export const EXPLORE_PATH = '/'

/** A listagem completa, paginada. Constante própria porque `${EXPLORE_PATH}/todos` viraria `//todos`. */
export const EXPLORE_ALL_PATH = '/todos'

/** Nome público do diretório, para títulos e textos. */
export const EXPLORE_NAME = 'BeWave Explore'

/**
 * Destino do CTA "Criar sua página" — landing, rodapé e fim da lista do
 * Explore.
 *
 * Aponta para o cadastro de criador de página (Explore::RegistrationsController):
 * a pessoa cria a conta, confirma o e-mail e cai no painel do criador. O
 * endereço antigo (`/explore/criar-pagina`, a página "em construção") virou 301
 * para cá em routes.rb.
 */
export const CREATE_PAGE_PATH = '/criar-conta'

/**
 * As duas portas de entrada da BeWave, e o que fica atrás de cada uma.
 *
 * São produtos distintos com autenticações distintas: o admin é o painel da
 * escola (cookie `session_token`, tabela users); o Explore é a conta de criador
 * de página (cookie `creator_session_token`, tabela creators). Quem chega pela
 * landing não tem como adivinhar isso — é o menu do header (PublicUserMenu) que
 * mostra as duas lado a lado.
 */
export const ADMIN_SIGN_IN_PATH = '/sign_in'
export const CREATOR_SIGN_IN_PATH = '/entrar'
export const CREATOR_SIGN_OUT_PATH = '/sair'
/** Minhas Páginas — a home do criador logado (o antigo /painel é 301 pra cá). */
export const CREATOR_PAGES_PATH = '/minhas-paginas'
/** "Fale sobre você": o perfil é da CONTA e vale para todas as páginas dela. */
export const CREATOR_PROFILE_PATH = '/perfil'

/**
 * Lojas do app do aluno.
 *
 * Ficam aqui porque mais de uma tela pública manda baixar o app — a página da
 * escola (no diálogo pós-cadastro) e a landing. Estavam copiadas dentro da
 * página da escola; um link só de um lado é o tipo de divergência que ninguém
 * percebe até o app mudar de ID.
 */
export const APP_STORE_URL = 'https://apps.apple.com/us/app/bewave/id6760758451'
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.beewave.bewavescholl'

/**
 * WhatsApp da BeWave. Mesmo motivo das lojas acima: o rodapé e os planos da home
 * mandam para cá, e um número desatualizado de um lado só é do tipo de erro que
 * ninguém percebe.
 */
export const BEWAVE_WHATSAPP_URL = 'https://wa.me/558591605858'

/**
 * Canal do YouTube. `@BeWaveapp` é o handle escolhido; `@BeWave-b2j`, que estava
 * aqui antes, é o automático que o YouTube gera na criação. Os dois resolvem para
 * o mesmo canal (UCBl24w3BQ0KQyBSLrMRKpbg), então nada estava quebrado — só feio.
 *
 * Subiu de BewaveFooter para cá quando a seção de shorts passou a precisar do
 * mesmo endereço: dois lugares apontando para o canal é exatamente o caso que
 * este arquivo existe para evitar.
 */
export const BEWAVE_YOUTUBE_URL = 'https://www.youtube.com/@BeWaveapp'
export const BEWAVE_YOUTUBE_SHORTS_URL = `${BEWAVE_YOUTUBE_URL}/shorts`

/**
 * Link do WhatsApp já com o assunto escrito.
 *
 * Sem o texto, a conversa começa perguntando o que a pessoa acabou de dizer ao
 * clicar — qual plano quer, que quer uma reunião, que quer a BeWave na escola.
 */
export function whatsappHref(message: string): string {
  return `${BEWAVE_WHATSAPP_URL}?text=${encodeURIComponent(message)}`
}

/**
 * Gradiente dos painéis de banner da landing (o carrossel de apps, o convite
 * para escolas). Fica como `style`, não classe utilitária, porque é cor fixa da
 * landing — diferente da página da escola, onde o gradiente sai de
 * `school.primary_color` e muda por escola.
 *
 * As cores saem dos tokens de `application.css`, os mesmos que o "Como funciona"
 * usa no destaque. O literal depois da vírgula é só rede de segurança: se o
 * token sumir, o gradiente cai na cor certa em vez de ficar transparente.
 */
export const PANEL_GRADIENT =
  'linear-gradient(90deg, var(--color-lp-blue, #012FAE) 0%, var(--color-lp-blue-deep, #1847AC) 100%)'

/**
 * Fundo dos painéis escuros da landing (o hero e a seção da plataforma).
 *
 * Duas camadas, como saiu do Figma: o gradiente azul translúcido por cima de um
 * petróleo sólido. O petróleo é o que sustenta o contraste do texto branco — só o
 * gradiente, com 50% de alpha, deixaria tudo lavado.
 *
 * Literal e não montado dos tokens `--color-lp-blue*` porque são as mesmas cores
 * com alpha, e `rgba()` de um token exigiria reescrever os tokens em canais
 * separados só para isso.
 */
export const BRAND_PANEL_BACKGROUND =
  'linear-gradient(90deg, rgba(1, 47, 174, 0.50) -1.94%, rgba(24, 71, 172, 0.50) 100%), #072F33'

/**
 * Nota exibida enquanto não existe sistema de avaliação.
 *
 * É valor fixo, não dado: nenhuma escola foi avaliada e o backend não envia nota.
 * Fica numa constante única, usada pela listagem e pela página da escola, para
 * sair das duas de uma vez quando as avaliações reais existirem.
 */
export const PLACEHOLDER_RATING = '5,0'
